import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { CatButton } from "@cat-digital-blocks/react/bedrock/components/Button";
import { CatHeading } from "@cat-digital-blocks/react/bedrock/components/Heading";
import { CatTabs } from "@cat-digital-blocks/react/bedrock/components/Tabs";
import { CatTab } from "@cat-digital-blocks/react/bedrock/components/Tab";
import { CatTabPanel } from "@cat-digital-blocks/react/bedrock/components/TabPanel";
import { CatTable } from "@cat-digital-blocks/react/bedrock/components/Table";
import { CatTableObject } from "@cat-digital-blocks/react/bedrock/components/TableObject";
import { CatTableBody } from "@cat-digital-blocks/react/bedrock/components/TableBody";
import { CatTableCell } from "@cat-digital-blocks/react/bedrock/components/TableCell";
import { CatTableHeader } from "@cat-digital-blocks/react/bedrock/components/TableHeader";
import { CatTableHeaderCell } from "@cat-digital-blocks/react/bedrock/components/TableHeaderCell";
import { CatTableRow } from "@cat-digital-blocks/react/bedrock/components/TableRow";
import { CatInlineNotification } from "@cat-digital-blocks/react/bedrock/components/InlineNotification";
import { CatLoadingIndicator } from "@cat-digital-blocks/react/bedrock/components/LoadingIndicator";
import { CatIconClose } from "@cat-digital-blocks/react/bedrock/components/Icons/Close";
import { CatIconEdit } from "@cat-digital-blocks/react/bedrock/components/Icons/Edit";
import {
  useGetAttributeMappingsQuery,
  useDeleteAttributeMappingMutation,
} from "@services/dcmApi";
import type { AttributeMappingResponse } from "@/types/api";
import styles from "./AttributeRegistration.module.scss";
import CreateAttributeModal from "./CreateAttributeModal";
import EditAttributeModal from "./EditAttributeModal";

// PAL Types for nested tabs
const PAL_TYPES = ["LEADS", "OPPORTUNITY", "TASKS", "CASE"] as const;
type PalType = (typeof PAL_TYPES)[number];

interface AttributeRegistrationProps {
  currentUserName?: string;
}

const AttributeRegistration = ({
  currentUserName,
}: AttributeRegistrationProps) => {
  const { t } = useTranslation();

  const [selectedPalTypeIndex, setSelectedPalTypeIndex] = useState(0);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [modalPalType, setModalPalType] = useState<PalType>("LEADS");
  const [editingAttribute, setEditingAttribute] =
    useState<AttributeMappingResponse | null>(null);

  // Store the intended tab index to prevent unwanted changes
  const intendedTabIndex = useRef(selectedPalTypeIndex);

  // Update the ref whenever we intentionally change the tab
  useEffect(() => {
    intendedTabIndex.current = selectedPalTypeIndex;
  }, [selectedPalTypeIndex]);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  const selectedPalType: PalType = PAL_TYPES[selectedPalTypeIndex];

  // Fetch attribute mappings for the selected PAL type
  const {
    data: attributeMappings = [],
    isLoading,
    isFetching,
    error,
    refetch: refetchMappings,
  } = useGetAttributeMappingsQuery(
    { palType: selectedPalType },
    {
      refetchOnMountOrArgChange: true,
      pollingInterval: 0,
    },
  );

  // Debug logging
  useEffect(() => {
    console.log("AttributeRegistration - selectedPalType:", selectedPalType);
    console.log(
      "AttributeRegistration - attributeMappings:",
      attributeMappings,
    );
    console.log("AttributeRegistration - isLoading:", isLoading);
    console.log("AttributeRegistration - isFetching:", isFetching);
  }, [selectedPalType, attributeMappings, isLoading, isFetching]);

  const [deleteAttribute, { isLoading: isDeleting }] =
    useDeleteAttributeMappingMutation();

  const handlePalTypeTabChange = useCallback(
    (event: CustomEvent) => {
      // Stop propagation to prevent bubbling to parent DataSourceRegistration tabs
      event.stopPropagation();

      // Get the active tab index from event detail
      const newIndex = event.detail?.activeTabIndex;

      if (typeof newIndex !== "number") {
        return; // Ignore events without proper tab index
      }

      // Ignore tab changes when modal is open - the state should not change
      if (isCreateModalOpen || editingAttribute) {
        return;
      }

      if (newIndex >= 0 && newIndex < PAL_TYPES.length) {
        intendedTabIndex.current = newIndex;
        setSelectedPalTypeIndex(newIndex);
      }
    },
    [isCreateModalOpen, editingAttribute],
  );

  // Handle Add Attribute button click - accepts specific palType and opens modal
  const handleAddAttributeClick = useCallback(
    (palType: PalType) => (event: MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();

      // Find the index of the palType and ensure it stays selected
      const palTypeIndex = PAL_TYPES.indexOf(palType);
      if (palTypeIndex !== -1) {
        setSelectedPalTypeIndex(palTypeIndex);
      }

      setModalPalType(palType);
      setIsCreateModalOpen(true);
    },
    [],
  );

  const handleDeleteAttribute = useCallback(
    async (attribute: AttributeMappingResponse) => {
      if (
        !window.confirm(
          t("admin.attributeRegistration.confirmDelete", {
            attribute: attribute.mappingColumn,
          }),
        )
      ) {
        return;
      }
      try {
        await deleteAttribute({ attributeId: attribute.id }).unwrap();
        setNotification({
          type: "success",
          message: t("admin.attributeRegistration.deleteSuccess"),
        });
        refetchMappings();
        setTimeout(() => setNotification(null), 5000);
      } catch {
        setNotification({
          type: "error",
          message: t("admin.attributeRegistration.deleteError"),
        });
      }
    },
    [deleteAttribute, refetchMappings, t],
  );

  const handleCreateSuccess = useCallback(() => {
    setIsCreateModalOpen(false);
    setNotification({
      type: "success",
      message: t("admin.attributeRegistration.createSuccess"),
    });
    refetchMappings();
    setTimeout(() => setNotification(null), 5000);
  }, [refetchMappings, t]);

  const handleEditSuccess = useCallback(() => {
    setEditingAttribute(null);
    setNotification({
      type: "success",
      message: t("admin.attributeRegistration.updateSuccess"),
    });
    refetchMappings();
    setTimeout(() => setNotification(null), 5000);
  }, [refetchMappings, t]);

  // Sort attribute mappings by mapping column
  const sortedMappings = useMemo(() => {
    return [...attributeMappings].sort((a, b) =>
      (a.mappingColumn || "").localeCompare(b.mappingColumn || ""),
    );
  }, [attributeMappings]);

  const renderTable = () => {
    if (error) {
      return (
        <CatInlineNotification variant="error">
          {t("admin.attributeRegistration.error.loadFailed")}
        </CatInlineNotification>
      );
    }

    return (
      <CatTableObject minWidth="100%">
        <CatTable
          className={styles.attributeTable}
          key={`table-${selectedPalType}`}
          arialabel={t("admin.attributeRegistration.table.ariaLabel")}
        >
          <CatTableHeader isFrozen>
            <CatTableRow isLoading={isLoading}>
              <CatTableHeaderCell width="18%" isFrozen>
                {t("admin.attributeRegistration.table.dataSource")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="18%" isFrozen>
                {t("admin.attributeRegistration.table.mappingColumn")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="22%" isFrozen>
                {t("admin.attributeRegistration.table.description")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="15%" isFrozen>
                {t("admin.attributeRegistration.table.operators")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="17%" isFrozen>
                {t("admin.attributeRegistration.table.allowedValues")}
              </CatTableHeaderCell>
              <CatTableHeaderCell width="10%" isFrozen>
                {t("admin.attributeRegistration.table.actions")}
              </CatTableHeaderCell>
            </CatTableRow>
          </CatTableHeader>
          <CatTableBody>
            {isLoading ? (
              // Show loading indicator
              <CatTableRow>
                <CatTableCell colspan={6} className={styles.loadingRow}>
                  <CatLoadingIndicator />
                </CatTableCell>
              </CatTableRow>
            ) : sortedMappings.length === 0 ? (
              <CatTableRow>
                <CatTableCell colspan={6} className={styles.emptyRow}>
                  {t("admin.attributeRegistration.noAttributes")}
                </CatTableCell>
              </CatTableRow>
            ) : (
              sortedMappings.map((attr) => (
                <CatTableRow key={attr.id}>
                  <CatTableCell>{attr.sourceName || "-"}</CatTableCell>
                  <CatTableCell>{attr.mappingColumn || "-"}</CatTableCell>
                  <CatTableCell>{attr.description || "-"}</CatTableCell>
                  <CatTableCell>
                    {attr.allowedOperators?.length
                      ? attr.allowedOperators.join(", ")
                      : "-"}
                  </CatTableCell>
                  <CatTableCell>
                    {attr.allowedValues?.length
                      ? attr.allowedValues.join(", ")
                      : "-"}
                  </CatTableCell>
                  <CatTableCell>
                    <div className={styles.actionButtons}>
                      <CatButton
                        variant="ghost"
                        size="sm"
                        onClick={() => setEditingAttribute(attr)}
                        title={t("common.edit")}
                      >
                        <CatIconEdit size="sm" />
                      </CatButton>
                      <CatButton
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteAttribute(attr)}
                        disabled={isDeleting}
                        title={t("common.delete")}
                      >
                        <CatIconClose size="sm" />
                      </CatButton>
                    </div>
                  </CatTableCell>
                </CatTableRow>
              ))
            )}
          </CatTableBody>
        </CatTable>
      </CatTableObject>
    );
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <CatHeading variant="title-sm">
          {t("admin.attributeRegistration.title")}
        </CatHeading>
      </div>

      {notification && (
        <CatInlineNotification
          variant={notification.type === "success" ? "success" : "error"}
          className={styles.notification}
        >
          {notification.message}
        </CatInlineNotification>
      )}

      {/* Nested PAL Type Tabs with TabPanel pattern */}
      <div className={styles.palTypeTabs}>
        <CatTabs
          id="pal-type-tabs"
          activeIndex={selectedPalTypeIndex}
          onBlTabChange={handlePalTypeTabChange}
        >
          <CatTab>{t("palTypes.leads")}</CatTab>
          <CatTabPanel slot="panel">
            <div className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <span className={styles.palTypeLabel}>
                  {t("admin.attributeRegistration.attributesFor", {
                    palType: "LEADS",
                  })}
                </span>
                <div
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CatButton
                    variant="primary"
                    onClick={handleAddAttributeClick("LEADS")}
                  >
                    {t("admin.attributeRegistration.addAttribute")}
                  </CatButton>
                </div>
              </div>
              {renderTable()}
            </div>
          </CatTabPanel>

          <CatTab>{t("palTypes.opportunity")}</CatTab>
          <CatTabPanel slot="panel">
            <div className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <span className={styles.palTypeLabel}>
                  {t("admin.attributeRegistration.attributesFor", {
                    palType: "OPPORTUNITY",
                  })}
                </span>
                <div
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CatButton
                    variant="primary"
                    onClick={handleAddAttributeClick("OPPORTUNITY")}
                  >
                    {t("admin.attributeRegistration.addAttribute")}
                  </CatButton>
                </div>
              </div>
              {renderTable()}
            </div>
          </CatTabPanel>

          <CatTab>{t("palTypes.tasks")}</CatTab>
          <CatTabPanel slot="panel">
            <div className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <span className={styles.palTypeLabel}>
                  {t("admin.attributeRegistration.attributesFor", {
                    palType: "TASKS",
                  })}
                </span>
                <div
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CatButton
                    variant="primary"
                    onClick={handleAddAttributeClick("TASKS")}
                  >
                    {t("admin.attributeRegistration.addAttribute")}
                  </CatButton>
                </div>
              </div>
              {renderTable()}
            </div>
          </CatTabPanel>

          <CatTab>{t("palTypes.case")}</CatTab>
          <CatTabPanel slot="panel">
            <div className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <span className={styles.palTypeLabel}>
                  {t("admin.attributeRegistration.attributesFor", {
                    palType: "CASE",
                  })}
                </span>
                <div
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <CatButton
                    variant="primary"
                    onClick={handleAddAttributeClick("CASE")}
                  >
                    {t("admin.attributeRegistration.addAttribute")}
                  </CatButton>
                </div>
              </div>
              {renderTable()}
            </div>
          </CatTabPanel>
        </CatTabs>
      </div>

      {/* Create Attribute Modal - keyed by modalPalType to ensure fresh state */}
      {isCreateModalOpen && (
        <CreateAttributeModal
          key={`create-${modalPalType}`}
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleCreateSuccess}
          palType={modalPalType}
          userName={currentUserName}
        />
      )}

      {/* Edit Attribute Modal */}
      {editingAttribute && (
        <EditAttributeModal
          isOpen={!!editingAttribute}
          onClose={() => setEditingAttribute(null)}
          onSuccess={handleEditSuccess}
          attribute={editingAttribute}
          userName={currentUserName}
        />
      )}
    </div>
  );
};

export default AttributeRegistration;
